import { useTranslation } from '@wix/fe-essentials/react';
import { isExternalUrl } from '@wix/rules-panel-ui';
import { useMemo } from 'react';
import { BiCtaNames } from '../bi/types';
import { getFixerDesignPropsFromCapabilities } from '../utils/get-fixer-design-props';
import { originalImageUrl, resolveImageUrl } from '../utils/resolve-image-url';
import { useA11yWizardAdapter } from './use-a11y-wizard-adapter';
import { useBiLogger } from './use-bi-logger';
/**
 * Hook that builds the AltTextFixerContextValue for a11y-wizard
 *
 * This encapsulates all the a11y-wizard specific logic for wiring up
 * the shared alt-text fixer components with:
 * - BI logging (using a11y-wizard's biLogger)
 * - Translations (using a11y-wizard's i18n keys)
 * - Design props (based on editor host)
 */
export const useA11yAltTextContext = function (_temp) {
  let {
    handleCtaClick
  } = _temp === void 0 ? {} : _temp;
  const {
    t
  } = useTranslation();
  const {
    capabilities,
    editorHost
  } = useA11yWizardAdapter();
  const biLogger = useBiLogger();
  const designProps = getFixerDesignPropsFromCapabilities(capabilities);
  const imagesGap = capabilities.wizardPresentation.imageGridGap;
  return useMemo(() => ({
    biCallbacks: {
      onCancel: (elementCount, failedElementCount) => {
        handleCtaClick == null || handleCtaClick(BiCtaNames.Cancel);
        biLogger.taskCtaClick({
          ctaName: BiCtaNames.Cancel,
          elementCount,
          failedElementCount
        });
      },
      onEdit: (elementCount, failedElementCount) => {
        handleCtaClick == null || handleCtaClick(BiCtaNames.Edit);
        biLogger.taskCtaClick({
          ctaName: BiCtaNames.Edit,
          elementCount,
          failedElementCount
        });
      },
      onGetStarted: (elementCount, failedElementCount) => {
        handleCtaClick == null || handleCtaClick(BiCtaNames.GetStarted);
        biLogger.taskCtaClick({
          ctaName: BiCtaNames.GetStarted,
          elementCount,
          failedElementCount
        });
      }
    },
    designProps: {
      imageSize: 50,
      imagesGap,
      ruleFooter: designProps == null ? void 0 : designProps.ruleFooter
    },
    editorHost,
    imageUtils: {
      isExternalUrl,
      originalImageUrl,
      resolveImageUrl
    },
    translations: {
      cancelButton: t('accessibility-wizard.rules-list.alt-text.button.cancel'),
      editButton: t('accessibility-wizard.alt-text.completed-task.edit'),
      filledProgress: (filled, total) => t('accessibility-wizard.alt-text.overview-section.progress-bar.filled', {
        filledAmount: filled,
        totalAmount: total
      }),
      getStartedButton: t('accessibility-wizard.rules-list.alt-text.button.get-started')
    },
    wizardPresentation: capabilities.wizardPresentation
  }), [t, editorHost, biLogger, capabilities.wizardPresentation, designProps, imagesGap, handleCtaClick]);
};
//# sourceMappingURL=use-a11y-alt-text-context.js.map